import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';

// FullAdmin's global view of every user, every school, every role — via
// /api/admin/users. Lives in the /admin/main area (see
// MainAdminDashboard.js), separate from any single school's dashboard.
// Can create/edit FullAdmins and edit anyone's role or school assignments.
// SchoolAdmins manage their own school's team instead, from
// /admin/:schoolId/users (see Users.js) — a much smaller, school-scoped
// capability with no role/assignment editing.
function GlobalUsers() {
  const { user: currentUser } = useAuth();
  const { schools } = useSchool();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const emptyForm = {
    username: '',
    password: '',
    full_name: '',
    email: '',
    is_active: true,
    role: 'school_admin',
    school_ids: []
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load users');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWarning('');

    try {
      const submitData = { ...formData };
      if (editingUser && !submitData.password) {
        delete submitData.password;
      }
      if (editingUser) {
        await axios.put(`/api/admin/users/${editingUser.id}`, submitData);
      } else {
        // Password isn't sent on create at all — the backend generates one
        // and emails it to the new user directly (see CreateUser). editingUser's
        // password field is a separate, legitimate "reset this user's
        // password" feature and is unaffected.
        delete submitData.password;
        const response = await axios.post('/api/admin/users', submitData);
        if (response.data?.warning) {
          setWarning(response.data.warning);
        }
      }

      setFormData(emptyForm);
      setShowForm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't pre-fill password
      full_name: user.full_name,
      email: user.email || '',
      is_active: user.is_active,
      role: user.role || 'school_admin',
      school_ids: (user.schools || []).map(s => s.id)
    });
    setShowForm(true);
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This removes their account entirely.`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${id}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData(emptyForm);
    setError('');
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setWarning('');
    setShowForm(true);
  };

  const toggleFormSchool = (id) => {
    setFormData(prev => ({
      ...prev,
      school_ids: prev.school_ids.includes(id)
        ? prev.school_ids.filter(s => s !== id)
        : [...prev.school_ids, id]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>User Management</h2>
        <button
          onClick={handleAddNew}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Add New User
        </button>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {warning && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          ⚠️ {warning}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            {!editingUser && (
              <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '-0.5rem' }}>
                A secure password will be generated automatically and emailed to them along with their login link.
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Username:
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  required
                  disabled={!!editingUser} // Don't allow username changes
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: editingUser ? '#f8f9fa' : 'white'
                  }}
                />
              </div>

              {editingUser && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Password:
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Leave blank to keep current password"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Full Name:
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Email:
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required={!editingUser}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Role:
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="school_admin">SchoolAdmin (manages assigned schools)</option>
                  <option value="full_admin">FullAdmin (manages everything)</option>
                </select>
              </div>

              {formData.role === 'school_admin' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Schools:
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {schools.map(school => (
                      <label key={school.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={formData.school_ids.includes(school.id)}
                          onChange={() => toggleFormSchool(school.id)}
                        />
                        {school.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {editingUser && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <span style={{ fontWeight: 'bold' }}>Active</span>
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No users found.
          </div>
        ) : (
          users.map(user => (
            <div
              key={user.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: user.is_active ? 'white' : '#f8f9fa'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: user.is_active ? '#333' : '#666' }}>
                    {user.full_name}
                    {!user.is_active && (
                      <span style={{ color: '#dc3545', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (Inactive)
                      </span>
                    )}
                    {user.role === 'full_admin' && (
                      <span style={{ color: '#007bff', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (FullAdmin)
                      </span>
                    )}
                    {user.username === currentUser?.username && (
                      <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (You)
                      </span>
                    )}
                  </h3>
                  <p style={{ margin: '0', color: '#666', fontFamily: 'monospace' }}>
                    @{user.username}
                  </p>
                  {user.email && (
                    <p style={{ margin: '0.25rem 0 0 0', color: '#666' }}>
                      {user.email}
                    </p>
                  )}
                  {user.role === 'school_admin' && (
                    <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.85rem' }}>
                      Schools: {(user.schools || []).map(s => s.name).join(', ') || 'none assigned'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(user)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(user.id, user.username)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                Created: {formatDate(user.created_at)}
                {user.updated_at !== user.created_at && (
                  <span> • Updated: {formatDate(user.updated_at)}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default GlobalUsers;
